import {
  DocumentNode,
  OperationVariables,
  QueryHookOptions,
  SubscriptionHookOptions,
  TypedDocumentNode,
  useSubscription as useApolloSubscription,
  useQuery as useApolloQuery,
  SubscriptionResult,
  QueryResult,
} from '@apollo/client';
import axios from 'axios';
import Bluebird from 'bluebird';
import _, {isEqual, memoize} from 'lodash';
import {useEffect, useState} from 'react';
import {itoh} from 'src/utils';

function isMissingMetadata(data: {
  uri?: string | null;
  imageUri?: string | null;
  name?: string | null;
  title?: string | null;
}) {
  return (
    _.isString(data.uri) && (data.imageUri === null || data.name === null || data.title === null)
  );
}

function isArtist(metadata: {short_description?: string | null}) {
  return !!metadata.short_description;
}

const fetchMetadata = memoize(async (uri) => {
  try {
    const url = itoh(uri);
    if (!url) {
      return;
    }

    const {data} = await axios.get(url);
    return data;
  } catch (err) {
    return null;
  }
});

async function asyncMapValues(obj: any, mapper: (o: any) => Promise<void>): Promise<typeof obj> {
  if (_.isArray(obj)) {
    return Bluebird.map(obj, (o) => asyncMapValues(o, mapper), {concurrency: 5});
  } else if (_.isPlainObject(obj)) {
    const cloned = _.clone(obj);
    await mapper(cloned);
    for (const k of Object.keys(cloned)) {
      cloned[k] = await asyncMapValues(cloned[k], mapper);
    }
    return cloned;
  } else {
    return obj;
  }
}

async function mapper(obj: any) {
  if (isMissingMetadata(obj)) {
    const metadata = await fetchMetadata(obj.uri);
    if (metadata) {
      if (isArtist(metadata)) {
        obj.name = metadata.name;
        obj.description = metadata.description;
        obj.imageUri = metadata.image;
      } else {
        obj.title = obj.name;
        obj.realArtist = obj.description;
        obj.imageUri = obj.image;
      }
    }
  }
}

function useMetadataAugment(data: any) {
  const [augmented, setAugmented] = useState<typeof data>(data);
  const [augmenting, setIsAugmenting] = useState(false);

  useEffect(() => {
    async function augment() {
      setIsAugmenting(true);
      const auged = await asyncMapValues(_.cloneDeep(data), mapper);
      setAugmented(isEqual(auged, data) ? data : auged);
      setIsAugmenting(false);
    }
    augment();
  }, [data]);

  return {augmented, augmenting};
}

export function useSubscription<TData = any, TVariables = OperationVariables>(
  subscription: DocumentNode | TypedDocumentNode<TData, TVariables>,
  options?: SubscriptionHookOptions<TData, TVariables>,
): SubscriptionResult<TData> {
  const {data, ...rest} = useApolloSubscription(subscription, options);
  const {augmented} = useMetadataAugment(data);

  return {
    ...rest,
    data: augmented,
  };
}

export function useQuery<TData = any, TVariables = OperationVariables>(
  query: DocumentNode | TypedDocumentNode<TData, TVariables>,
  options?: QueryHookOptions<TData, TVariables>,
): QueryResult<TData> {
  const {data, ...rest} = useApolloQuery(query, options);
  const {augmented} = useMetadataAugment(data);

  return {
    ...rest,
    data: augmented,
  };
}
